import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { IGridDevice } from 'app/shared/model/grid-device.model';

export interface IGridDevicesState {
  loading: boolean;
  errorMessage: string | null;
  gridId: number | null;
  devices: ReadonlyArray<IGridDevice>;
  page: number;
  size: number;
  // null means the backend didn't return an x-total-count header, so the true total is unknown.
  totalItems: number | null;
}

const initialState: IGridDevicesState = {
  loading: false,
  errorMessage: null,
  gridId: null,
  devices: [],
  page: 0,
  size: 10,
  totalItems: null,
};

export interface IGetGridDevicesParams {
  gridId: number;
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
}

// The backend returns a plain array (pagination communicated via the x-total-count header,
// same convention as the other JHipster-generated entity endpoints) using deviceId/deviceName
// field names rather than the id/name shape described in the API spec.
interface IRawGridDevice {
  id?: number;
  deviceId?: number;
  name?: string;
  deviceName?: string | null;
  serialNumber?: string;
  state?: string;
  latitude?: string;
  longitude?: string;
}

const normalizeDevice = (raw: IRawGridDevice): IGridDevice => ({
  id: raw.id ?? raw.deviceId,
  name: raw.name ?? raw.deviceName ?? undefined,
  serialNumber: raw.serialNumber,
  state: raw.state,
  latitude: raw.latitude,
  longitude: raw.longitude,
});

const apiUrl = 'api/grids';

export const getGridDevices = createAsyncThunk(
  'gridDevices/fetch_devices',
  async ({ gridId, page = 0, size = 10, sort = 'name,asc', search }: IGetGridDevicesParams) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const requestUrl = `${apiUrl}/${gridId}/devices?page=${page}&size=${size}&sort=${sort}${searchParam}`;
    return axios.get<IRawGridDevice[]>(requestUrl);
  },
);

export const GridDevicesSlice = createSlice({
  name: 'gridDevices',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isPending(getGridDevices), (state, action) => {
        state.loading = true;
        state.errorMessage = null;
        state.gridId = action.meta.arg.gridId;
      })
      .addMatcher(isFulfilled(getGridDevices), (state, action) => {
        // Ignore stale responses from a grid/page that is no longer the latest requested one.
        if (action.meta.arg.gridId !== state.gridId) {
          return;
        }
        const { data, headers } = action.payload;
        const devices = (data ?? []).map(normalizeDevice);
        const parsedTotal = parseInt(headers?.['x-total-count'], 10);
        state.loading = false;
        state.devices = devices;
        state.page = action.meta.arg.page ?? 0;
        state.size = action.meta.arg.size ?? state.size;
        state.totalItems = Number.isNaN(parsedTotal) ? null : parsedTotal;
      })
      .addMatcher(isRejected(getGridDevices), (state, action) => {
        if (action.meta.arg.gridId !== state.gridId) {
          return;
        }
        state.loading = false;
        state.errorMessage = action.error?.message;
        state.devices = [];
        state.totalItems = null;
      });
  },
});

export const { reset } = GridDevicesSlice.actions;

export default GridDevicesSlice.reducer;
